import {
  ACCOUNT_BALANCE_LABELS,
  ACCOUNT_CLASSIFICATION_LABELS,
  ACCOUNT_SUBTYPE_LABELS,
  ACCOUNT_TYPE_LABELS,
  REPORTING_ACCOUNT_TYPE_LABELS,
} from "./index"

import { AKROCHEM_COMPANY_NAME } from "@/constants/common"
import {
  GlAccountBalance,
  GlAccountClassification,
  GlAccountCurrency,
  GlAccountSubType,
  GlAccountType,
  ReportingAccountType,
} from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

// Reporting Account Type options
export const REPORTING_ACCOUNT_TYPE_OPTIONS: Option<string>[] = [
  { value: ReportingAccountType.Parent, label: REPORTING_ACCOUNT_TYPE_LABELS[ReportingAccountType.Parent] },
  { value: ReportingAccountType.Posting, label: REPORTING_ACCOUNT_TYPE_LABELS[ReportingAccountType.Posting] },
]

// Company options (read-only, future-proofed for multi-company)
export const COMPANY_OPTIONS: Option<string>[] = [{ value: AKROCHEM_COMPANY_NAME, label: AKROCHEM_COMPANY_NAME }]

// Type options
export const ACCOUNT_TYPE_OPTIONS: Option<string>[] = [
  { value: GlAccountType.Asset, label: ACCOUNT_TYPE_LABELS[GlAccountType.Asset] },
  { value: GlAccountType.Liability, label: ACCOUNT_TYPE_LABELS[GlAccountType.Liability] },
  { value: GlAccountType.Equity, label: ACCOUNT_TYPE_LABELS[GlAccountType.Equity] },
  { value: GlAccountType.Revenue, label: ACCOUNT_TYPE_LABELS[GlAccountType.Revenue] },
  { value: GlAccountType.Expense, label: ACCOUNT_TYPE_LABELS[GlAccountType.Expense] },
].sort((a, b) => a.label.localeCompare(b.label))

// Type-to-Subtype mapping (mirrors backend validation)
export const ACCOUNT_TYPE_TO_SUBTYPE: Record<GlAccountType, GlAccountSubType[]> = {
  [GlAccountType.Asset]: [
    GlAccountSubType.Cash,
    GlAccountSubType.AccountsReceivable,
    GlAccountSubType.OtherAssets,
    GlAccountSubType.Inventory,
    GlAccountSubType.FixedAssets,
  ],
  [GlAccountType.Liability]: [
    GlAccountSubType.Payables,
    GlAccountSubType.AccruedCorporateTaxes,
    GlAccountSubType.AccruedOther,
    GlAccountSubType.ReserveForFederalTaxes,
  ],
  [GlAccountType.Equity]: [GlAccountSubType.NetWorth],
  [GlAccountType.Revenue]: [GlAccountSubType.OtherRevenue, GlAccountSubType.Sales],
  [GlAccountType.Expense]: [
    GlAccountSubType.CostOfGoodsSold,
    GlAccountSubType.DepreciationAmortization,
    GlAccountSubType.MiscTaxes,
    GlAccountSubType.CapitalInsurance,
    GlAccountSubType.MedicalInsurance,
    GlAccountSubType.Advertising,
    GlAccountSubType.CorporateDonations,
    GlAccountSubType.InterestExpense,
    GlAccountSubType.BadDebt,
    GlAccountSubType.SalaryManagement,
    GlAccountSubType.SalaryPayPool,
    GlAccountSubType.SalarySales,
    GlAccountSubType.SalaryLab,
    GlAccountSubType.SalaryOffice,
    GlAccountSubType.SalaryWarehouse,
    GlAccountSubType.ProfitSharing,
    GlAccountSubType.CorporateReserve,
    GlAccountSubType.CorporateIncomeTax,
    GlAccountSubType.AccruedOverhead,
    GlAccountSubType.TravelEntertainment,
    GlAccountSubType.GeneralExpense,
    GlAccountSubType.Lifo,
    GlAccountSubType.WarehouseExpense,
    GlAccountSubType.ProgrammingExpense,
  ],
}

// All subtype options (for reference, not used in form)
export const ACCOUNT_SUBTYPE_OPTIONS: Option<string>[] = [
  { value: GlAccountSubType.Cash, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.Cash] },
  {
    value: GlAccountSubType.AccountsReceivable,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.AccountsReceivable],
  },
  { value: GlAccountSubType.OtherAssets, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.OtherAssets] },
  { value: GlAccountSubType.Inventory, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.Inventory] },
  { value: GlAccountSubType.FixedAssets, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.FixedAssets] },
  { value: GlAccountSubType.Payables, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.Payables] },
  {
    value: GlAccountSubType.AccruedCorporateTaxes,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.AccruedCorporateTaxes],
  },
  { value: GlAccountSubType.AccruedOther, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.AccruedOther] },
  {
    value: GlAccountSubType.ReserveForFederalTaxes,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.ReserveForFederalTaxes],
  },
  { value: GlAccountSubType.NetWorth, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.NetWorth] },
  { value: GlAccountSubType.OtherRevenue, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.OtherRevenue] },
  { value: GlAccountSubType.Sales, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.Sales] },
  {
    value: GlAccountSubType.CostOfGoodsSold,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.CostOfGoodsSold],
  },
  {
    value: GlAccountSubType.DepreciationAmortization,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.DepreciationAmortization],
  },
  { value: GlAccountSubType.MiscTaxes, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.MiscTaxes] },
  {
    value: GlAccountSubType.CapitalInsurance,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.CapitalInsurance],
  },
  {
    value: GlAccountSubType.MedicalInsurance,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.MedicalInsurance],
  },
  { value: GlAccountSubType.Advertising, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.Advertising] },
  {
    value: GlAccountSubType.CorporateDonations,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.CorporateDonations],
  },
  {
    value: GlAccountSubType.InterestExpense,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.InterestExpense],
  },
  { value: GlAccountSubType.BadDebt, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.BadDebt] },
  {
    value: GlAccountSubType.SalaryManagement,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.SalaryManagement],
  },
  { value: GlAccountSubType.SalaryPayPool, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.SalaryPayPool] },
  { value: GlAccountSubType.SalarySales, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.SalarySales] },
  { value: GlAccountSubType.SalaryLab, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.SalaryLab] },
  { value: GlAccountSubType.SalaryOffice, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.SalaryOffice] },
  {
    value: GlAccountSubType.SalaryWarehouse,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.SalaryWarehouse],
  },
  { value: GlAccountSubType.ProfitSharing, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.ProfitSharing] },
  {
    value: GlAccountSubType.CorporateReserve,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.CorporateReserve],
  },
  {
    value: GlAccountSubType.CorporateIncomeTax,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.CorporateIncomeTax],
  },
  {
    value: GlAccountSubType.AccruedOverhead,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.AccruedOverhead],
  },
  {
    value: GlAccountSubType.TravelEntertainment,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.TravelEntertainment],
  },
  {
    value: GlAccountSubType.GeneralExpense,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.GeneralExpense],
  },
  { value: GlAccountSubType.Lifo, label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.Lifo] },
  {
    value: GlAccountSubType.WarehouseExpense,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.WarehouseExpense],
  },
  {
    value: GlAccountSubType.ProgrammingExpense,
    label: ACCOUNT_SUBTYPE_LABELS[GlAccountSubType.ProgrammingExpense],
  },
].sort((a, b) => a.label.localeCompare(b.label))

// Classification options
export const ACCOUNT_CLASSIFICATION_OPTIONS: Option<string>[] = [
  {
    value: GlAccountClassification.BalanceSheet,
    label: ACCOUNT_CLASSIFICATION_LABELS[GlAccountClassification.BalanceSheet],
  },
  {
    value: GlAccountClassification.IncomeStatement,
    label: ACCOUNT_CLASSIFICATION_LABELS[GlAccountClassification.IncomeStatement],
  },
].sort((a, b) => a.label.localeCompare(b.label))

// Currency options (future-proofed for multiple currencies)
export const CURRENCY_OPTIONS: Option<string>[] = [{ value: GlAccountCurrency.Usd, label: "USD" }]

// Normal Balance options
export const NORMAL_BALANCE_OPTIONS: Option<string>[] = [
  { value: GlAccountBalance.Debit, label: ACCOUNT_BALANCE_LABELS[GlAccountBalance.Debit] },
  { value: GlAccountBalance.Credit, label: ACCOUNT_BALANCE_LABELS[GlAccountBalance.Credit] },
].sort((a, b) => a.label.localeCompare(b.label))
